import React from 'react';
import {
    DragDropContext,
    Draggable,
    Droppable,
    type DraggableProvided,
    type DraggableStateSnapshot,
    type DroppableProvided,
    type DropResult,
} from 'react-beautiful-dnd';

/** `react-beautiful-dnd` 与 React 18 类型声明不兼容，经 unknown 断言为 FC 供 JSX 使用（与菜单库详情一致） */
const ParkBindingDndContext = DragDropContext as unknown as React.FC<{
    onDragEnd: (result: DropResult) => void;
    children: React.ReactNode;
}>;
const ParkBindingDroppable = Droppable as unknown as React.FC<{
    droppableId: string;
    children: (provided: DroppableProvided) => React.ReactNode;
}>;
const ParkBindingDraggable = Draggable as unknown as React.FC<{
    draggableId: string;
    index: number;
    children: (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => React.ReactNode;
}>;

import { SecondMenu } from '../../models/second-menu.entity';
import { TaxGroup } from '../../models/tax-group.entity';
import { MenuListItem } from '../dish-menu-creator/menu-list/menu-list-component';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import _ from 'lodash';
import { FirstMenu } from '../../models/first-menu.entity';
import { apiManage_rbp } from '../../request';
import {
    Box,
    Button,
    Grid,
    IconButton,
    List,
    ListItemButton,
    Paper,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ReactComponent as MenuLibraryMenuNameIcon } from '../../static/images/svg/menu_library_detail_menu_name_icon.svg';
import { ReactComponent as EditIcon } from '../../static/images/svg/tabler_edit.svg';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import ParkModeFirstMenuSetting from './park-mode-first-menu.component';
import ExtraCostDetail from '../dish-menu-creator/extra-cost/extra-cost-detail.component';
import DialogComponent from '../../component/dialog/dialog.component';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import '../dish-menu-creator/extra-cost/scss/extra-cost.style.scss';
import './scss/park-mode-project-binding.scss';
import '../menu-library-management/scss/menu-library-add-subcategory-dialog.style.scss';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import setLoading from '../../component/linear-determinate';
import { RBP_MENU_SETTINGS, RBP_MENU_SETTINGS_TYPE } from '../../constants/rbp-menu-settings.constant';

/**
 * 娱乐项目管理 — 项目绑定（菜单 type=3，设置 id=148），交互与 RBP2 一致。
 */
class ParkModeProjectBindingComponent extends React.Component<Props, State> {
    public state: State = {
        menuData: [],
        taxGroup: [],
        isLoading: true,
        printGroup: [],
        isEditSluDialogOpen: false,
        editSluTargetId: null,
        editSluNameDraft: '',
        editSluSubmitting: false,
    };

    constructor(props: Props) {
        super(props);
        this.getMenuData = this.getMenuData.bind(this);
    }

    public async componentDidMount() {
        await this.init();
        await this.patchSecondMenuIndex();
    }

    public async init() {
        this.setState({ isLoading: true });
        try {
            setLoading(true);
            const taxResult = await this.getTaxGroup();
            const parkMenus = await this.getMenuData();
            const printGroup = await this.getPrintClass();
            const curMenuSlu: SecondMenu[] = _.get(parkMenus, '[0].menu_item_slu') || [];
            if (curMenuSlu.length > 0) {
                curMenuSlu.sort((a, b) => Number(a?.dmi_slu_index) - Number(b?.dmi_slu_index));
            }
            this.setState({
                activeMenuIndex: parkMenus.length > 0 ? '[0]' : '',
                taxGroup: taxResult,
                printGroup,
                menuData: curMenuSlu,
                firstMenuId: _.get(parkMenus, '[0].main_group_id'),
                isLoading: false,
            });
            setLoading(false);
        } catch {
            this.setState({ isLoading: false });
            setLoading(false);
        }
    }

    async patchSecondMenuIndex() {
        const { menuData } = this.state;
        let hasIndex = true;
        const menuIndexArr = menuData.map((item, index) => {
            if (!(item.dmi_slu_index || item.dmi_slu_index === 0)) {
                hasIndex = false;
            }
            return { dmi_slu_id: item.dmi_slu_id, dmi_slu_index: index + 1 };
        });
        if (!hasIndex) {
            await apiManage_rbp.patch_change_second_menu_index(menuIndexArr);
            const parkMenus = await this.getMenuData();
            this.setState({
                menuData: _.get(parkMenus, '[0].menu_item_slu') || [],
                firstMenuId: _.get(parkMenus, '[0].main_group_id'),
            });
        }
    }

    public async getTaxGroup() {
        const result = await apiManage_rbp.get_tax_group();
        if (!_.get(result, 'error')) {
            return result;
        }
        return [];
    }

    public async getPrintClass() {
        const result = await apiManage_rbp.getPrinterClass();
        if (!_.get(result, 'error')) {
            return result;
        }
        return [];
    }

    public async getMenuData() {
        const parkResult = await apiManage_rbp.get_menus_v3({
            type: RBP_MENU_SETTINGS_TYPE.PARK_MODE,
            with_dish: true,
        });
        if (!_.get(parkResult, 'error')) {
            return parkResult;
        }
        return [];
    }

    handleClick(_menuData: MenuListItem, index: string) {
        this.setState({ activeMenuIndex: '' }, () => {
            this.setState({ activeMenuIndex: index });
        });
    }

    private sluAsMenuListItem(slu: SecondMenu): MenuListItem {
        return {
            key: `${slu.dmi_slu_id}`,
            title: slu.dmi_slu_name ?? '',
            dataSource: slu,
        };
    }

    private getSubcategoryDialogSidebarOffset(): string {
        const mainPageData = MobxObjectsManager.get(MainPageState);
        return mainPageData.open
            ? mainPageData.sidebarWidth.expand
            : mainPageData.isMobileDevice
                ? mainPageData.sidebarWidth.hidden
                : mainPageData.sidebarWidth.reduced;
    }

    private openEditSluDialog = (slu: SecondMenu) => {
        this.setState({
            isEditSluDialogOpen: true,
            editSluTargetId: slu.dmi_slu_id as number,
            editSluNameDraft: slu.dmi_slu_name ?? '',
        });
    };

    private closeEditSluDialog = () => {
        if (this.state.editSluSubmitting) {
            return;
        }
        this.setState({
            isEditSluDialogOpen: false,
            editSluTargetId: null,
            editSluNameDraft: '',
        });
    };

    /** 新增子类：`editSluTargetId === null`；编辑：有目标 id */
    private submitSluDialog = async () => {
        const { editSluTargetId, editSluNameDraft, editSluSubmitting, menuData } = this.state;
        if (editSluSubmitting) {
            return;
        }
        const name = editSluNameDraft.trim();
        if (!name) {
            GlobalNotification.error(i18n.t('staff_setting_page_enter_type_name'));
            return;
        }

        if (editSluTargetId == null) {
            this.setState({ editSluSubmitting: true });
            try {
                const firstMenuId = this.state.firstMenuId ?? await this.handleAddFirstMenu();
                const maxSluIndex = menuData.reduce((max, x) => Math.max(max, Number(x.dmi_slu_index) || 0), 0);
                const newSecondMenu: SecondMenu = {
                    dmi_slu_name: name,
                    dmi_slu_index: maxSluIndex + 1,
                };
                if (!(firstMenuId || firstMenuId === 0)) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                const result = await apiManage_rbp.post_create_second_menu(firstMenuId, newSecondMenu);
                if (!_.get(result, 'error')) {
                    GlobalNotification.success(i18n.t('global_text_operate_success'));
                    const parkMenus = await this.getMenuData();
                    const { curMenuSlu, curActiveIndex } = this.getSortedMenuDataAndIndex(parkMenus, result.insertId);
                    this.setState({ activeMenuIndex: '' }, () => {
                        this.setState({
                            activeMenuIndex: `[${curActiveIndex}]`,
                            menuData: curMenuSlu,
                            firstMenuId: _.get(parkMenus, '[0].main_group_id'),
                            isEditSluDialogOpen: false,
                            editSluTargetId: null,
                            editSluNameDraft: '',
                        });
                    });
                } else {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                }
            } catch {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                this.setState({ editSluSubmitting: false });
            }
            return;
        }

        this.setState({ editSluSubmitting: true });
        try {
            const result = await apiManage_rbp.patch_second_menu(editSluTargetId, { dmi_slu_name: name });
            if (!_.get(result, 'error') && result.affectedRows > 0) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                const parkMenus = await this.getMenuData();
                const { curMenuSlu } = this.getSortedMenuDataAndIndex(parkMenus || []);
                this.setState({
                    menuData: curMenuSlu || [],
                    firstMenuId: _.get(parkMenus, '[0].main_group_id'),
                    isEditSluDialogOpen: false,
                    editSluTargetId: null,
                    editSluNameDraft: '',
                });
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ editSluSubmitting: false });
        }
    };

    private onSecondSluDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const from = result.source.index;
        const to = result.destination.index;
        if (from === to) {
            return;
        }
        const { menuData, activeMenuIndex } = this.state;
        /** 与菜单库一致：先乐观更新本地列表，避免 rbd 落位后先弹回原顺序再等接口 */
        const revertSnapshot = {
            menuData: menuData.map((x) => ({ ...x })),
            activeMenuIndex: activeMenuIndex ?? '',
        };
        const selectedDmiSluId = activeMenuIndex ? _.get(menuData, activeMenuIndex)?.dmi_slu_id : undefined;

        const reordered = Array.from(menuData);
        const [removed] = reordered.splice(from, 1);
        reordered.splice(to, 0, removed);
        const menuDataNext = reordered.map((item, i) => ({
            ...item,
            dmi_slu_index: i + 1,
        }));

        let newActiveMenuIndex = activeMenuIndex ?? '';
        if (selectedDmiSluId != null) {
            const idx = menuDataNext.findIndex((m) => m.dmi_slu_id === selectedDmiSluId);
            newActiveMenuIndex = idx >= 0 ? `[${idx}]` : '';
        }

        const params = menuDataNext.map((item) => ({
            dmi_slu_id: item.dmi_slu_id,
            dmi_slu_index: item.dmi_slu_index,
        })) as SecondMenu[];

        this.setState({
            menuData: menuDataNext,
            activeMenuIndex: newActiveMenuIndex,
        });

        void this.persistSecondMenuOrderAfterDrag(params, revertSnapshot);
    };

    private async persistSecondMenuOrderAfterDrag(
        params: SecondMenu[],
        revertSnapshot: { menuData: SecondMenu[]; activeMenuIndex: string },
    ) {
        try {
            const result = await apiManage_rbp.patch_change_second_menu_index(params);
            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                return;
            }
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } catch {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
        this.setState({
            menuData: revertSnapshot.menuData,
            activeMenuIndex: revertSnapshot.activeMenuIndex,
        });
    }

    async handleAddFirstMenu() {
        let firstMenuId: number | undefined;
        const newFirstMenu: FirstMenu = {
            main_group_name: i18n.t('extra_cost_page_label'),
            menu_item_slu: [],
            main_group_index: 0,
        };
        const result = await apiManage_rbp.post_create_first_menu(newFirstMenu);
        if (!_.get(result, 'error')) {
            const settingResult = await apiManage_rbp.patchSettingById(RBP_MENU_SETTINGS.PARK_MODE, {
                init: true,
                menu_id: result.insertId,
            });
            if (!_.get(settingResult, 'error')) {
                firstMenuId = result.insertId;
            }
        }
        return firstMenuId;
    }

    getSortedMenuDataAndIndex(firstMenu: FirstMenu[], curInsertId?: number) {
        const curMenuSlu: SecondMenu[] = _.get(firstMenu, '[0].menu_item_slu') || [];
        if (curMenuSlu.length > 0) {
            curMenuSlu.sort((a, b) => Number(a?.dmi_slu_index) - Number(b?.dmi_slu_index));
        }
        const curActiveIndex = curMenuSlu.findIndex((menuItem) => menuItem.dmi_slu_id === curInsertId);
        return { curMenuSlu, curActiveIndex };
    }

    public afterUpdateFirstMenu = async () => {
        await this.init();
    };

    render() {
        const {
            isLoading,
            menuData,
            activeMenuIndex,
            taxGroup,
            printGroup,
            isEditSluDialogOpen,
            editSluTargetId,
            editSluNameDraft,
            editSluSubmitting,
        } = this.state;
        const activeMenu = activeMenuIndex ? _.get(menuData, activeMenuIndex) : undefined;
        const subcategoryDialogSidebarOffset = this.getSubcategoryDialogSidebarOffset();
        return (
            <Box className="park-mode-project-binding">
                <ParkModeFirstMenuSetting afterOnSave={this.afterUpdateFirstMenu} />
                <Grid container spacing={3} sx={{ mt: 1, mb: 6 }}>
                    {isLoading ? (
                        <ParkModeProjectBindingSkeletonGrid />
                    ) : (
                        <>
                            <Grid item>
                                <Box className="park-mode-project-binding__subcategory-shell">
                                    <Paper elevation={0} className="park-mode-project-binding__subcategory-panel">
                                        <Stack
                                            direction="row"
                                            spacing={3}
                                            alignItems="center"
                                            className="park-mode-project-binding__subcategory-title"
                                        >
                                            <MenuLibraryMenuNameIcon className="park-mode-project-binding__subcategory-title-icon" />
                                            <Typography className="park-mode-project-binding__subcategory-title-text">
                                                {i18n.t('menu_library_detail_subcategory_title')}
                                            </Typography>
                                        </Stack>
                                        <Box className="park-mode-project-binding__subcategory-list-scroll">
                                            <List dense disablePadding component="div">
                                                <ListItemButton
                                                    className="park-mode-project-binding__slu-item park-mode-project-binding__slu-item--add"
                                                    aria-label={i18n.t('menu_library_detail_add_subcategory')}
                                                    onClick={() => {
                                                        this.setState({
                                                            isEditSluDialogOpen: true,
                                                            editSluTargetId: null,
                                                            editSluNameDraft: '',
                                                        });
                                                    }}
                                                >
                                                    <Box className="park-mode-project-binding__slu-item-inner">
                                                        <Typography
                                                            className="park-mode-project-binding__slu-item-label"
                                                            component="span"
                                                            variant="body1"
                                                        >
                                                            {i18n.t('menu_library_detail_add_subcategory')}
                                                        </Typography>
                                                        <AddRoundedIcon
                                                            className="park-mode-project-binding__slu-item-icon"
                                                            aria-hidden
                                                        />
                                                    </Box>
                                                </ListItemButton>
                                                <ParkBindingDndContext onDragEnd={this.onSecondSluDragEnd}>
                                                    <ParkBindingDroppable droppableId="park-mode-binding-second-slu">
                                                        {(dropProvided) => (
                                                            <div
                                                                ref={dropProvided.innerRef}
                                                                {...dropProvided.droppableProps}
                                                                style={{ minHeight: menuData.length ? undefined : 8 }}
                                                            >
                                                                {menuData.map((slu, index) => {
                                                                    const sid = slu.dmi_slu_id as number;
                                                                    const indexPath = `[${index}]`;
                                                                    const selected = activeMenuIndex === indexPath;
                                                                    return (
                                                                        <ParkBindingDraggable
                                                                            key={sid}
                                                                            draggableId={`park-binding-slu-${sid}`}
                                                                            index={index}
                                                                        >
                                                                            {(dragProvided, snapshot) => (
                                                                                <ListItemButton
                                                                                    component="div"
                                                                                    ref={dragProvided.innerRef}
                                                                                    {...dragProvided.draggableProps}
                                                                                    selected={selected}
                                                                                    onClick={() =>
                                                                                        this.handleClick(
                                                                                            this.sluAsMenuListItem(slu),
                                                                                            indexPath,
                                                                                        )
                                                                                    }
                                                                                    className="park-mode-project-binding__slu-item"
                                                                                    sx={{
                                                                                        ...(snapshot.isDragging
                                                                                            ? {
                                                                                                boxShadow: 2,
                                                                                                opacity: 0.95,
                                                                                            }
                                                                                            : {}),
                                                                                    }}
                                                                                >
                                                                                    <Box className="park-mode-project-binding__slu-item-inner park-mode-project-binding__slu-item-inner--with-edit">
                                                                                        <Typography
                                                                                            component="span"
                                                                                            variant="body1"
                                                                                            className="park-mode-project-binding__slu-item-label"
                                                                                        >
                                                                                            {slu.dmi_slu_name || '—'}
                                                                                        </Typography>
                                                                                        <Box
                                                                                            {...dragProvided.dragHandleProps}
                                                                                            aria-label={i18n.t(
                                                                                                'menu_library_detail_drag_sort_handle',
                                                                                            )}
                                                                                            sx={{
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                flexShrink: 0,
                                                                                                cursor: snapshot.isDragging
                                                                                                    ? 'grabbing'
                                                                                                    : 'grab',
                                                                                                mr: 0.5,
                                                                                                color: selected
                                                                                                    ? 'var(--icon-neutral-100)'
                                                                                                    : 'var(--text-primary-300)',
                                                                                                p: 1,
                                                                                                'svg': {
                                                                                                    fontSize: '24px',
                                                                                                },
                                                                                            }}
                                                                                            className="park-mode-project-binding__slu-item-drag-handle"
                                                                                        >
                                                                                            <DragIndicatorIcon fontSize="small" />
                                                                                        </Box>
                                                                                        <Tooltip
                                                                                            title={i18n.t(
                                                                                                'menu_library_detail_edit_subcategory',
                                                                                            )}
                                                                                        >
                                                                                            <IconButton
                                                                                                type="button"
                                                                                                size="small"
                                                                                                className="park-mode-project-binding__slu-item-edit"
                                                                                                aria-label={i18n.t(
                                                                                                    'menu_library_detail_edit_subcategory',
                                                                                                )}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    e.preventDefault();
                                                                                                    this.openEditSluDialog(slu);
                                                                                                }}
                                                                                            >
                                                                                                <EditIcon
                                                                                                    className="park-mode-project-binding__slu-item-edit-icon"
                                                                                                    aria-hidden
                                                                                                />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                </ListItemButton>
                                                                            )}
                                                                        </ParkBindingDraggable>
                                                                    );
                                                                })}
                                                                {dropProvided.placeholder as unknown as React.ReactNode}
                                                            </div>
                                                        )}
                                                    </ParkBindingDroppable>
                                                </ParkBindingDndContext>
                                            </List>
                                        </Box>
                                    </Paper>
                                </Box>
                            </Grid>
                            <Grid item xs>
                                {activeMenu && (
                                    <ExtraCostDetail
                                        variant="park-project-binding"
                                        taxGroup={taxGroup}
                                        printGroup={printGroup}
                                        detail={activeMenu}
                                        updateMenuData={async () => {
                                            const parkMenus = await this.getMenuData();
                                            const { curMenuSlu } = this.getSortedMenuDataAndIndex(parkMenus || []);
                                            this.setState({
                                                menuData: curMenuSlu || [],
                                                firstMenuId: _.get(parkMenus, '[0].main_group_id'),
                                            });
                                        }}
                                    />
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
                <DialogComponent
                    className="menu-library-add-subcategory-dialog-root park-mode-project-binding__edit-slu-dialog"
                    style={
                        {
                            ['--park-edit-slu-sidebar-offset' as string]: subcategoryDialogSidebarOffset,
                        } as React.CSSProperties
                    }
                    open={isEditSluDialogOpen}
                    fullWidth
                    maxWidth={false}
                    onClose={this.closeEditSluDialog}
                    PaperProps={{
                        className: 'park-mode-project-binding__edit-slu-dialog-paper',
                    }}
                    defaultHeaderProps={{
                        title:
                            editSluTargetId == null
                                ? i18n.t('menu_library_detail_add_subcategory')
                                : i18n.t('menu_library_detail_edit_subcategory'),
                        onClose: this.closeEditSluDialog,
                    }}
                    footer={(
                        <Box className="menu-library-add-subcategory-dialog__footer menu-library-add-subcategory-dialog__footer--full-width">
                            <Button
                                type="button"
                                variant="contained"
                                color="inherit"
                                disableElevation
                                className="menu-library-add-subcategory-dialog__btn-cancel"
                                disabled={editSluSubmitting}
                                onClick={this.closeEditSluDialog}
                            >
                                {i18n.t('menu_library_detail_add_subcategory_dialog_cancel')}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                className="menu-library-add-subcategory-dialog__btn-save"
                                disabled={editSluSubmitting}
                                startIcon={(
                                    <SaveIcon
                                        className="menu-library-add-subcategory-dialog__save-icon"
                                        aria-hidden
                                    />
                                )}
                                onClick={() => void this.submitSluDialog()}
                            >
                                {i18n.t('menu_library_detail_save_changes')}
                            </Button>
                        </Box>
                    )}
                >
                    <Box className="menu-library-add-subcategory-dialog menu-library-add-subcategory-dialog__body">
                        <FormFieldCell label={i18n.t('menu_library_create_dialog_subcategory_col_name')}>
                            <CommonInput
                                hiddenLabel
                                inputType="INPUT"
                                variant="outlined"
                                fullWidth
                                size="small"
                                placeholder={i18n.t('menu_library_create_dialog_subcategory_name_placeholder')}
                                value={editSluNameDraft}
                                disabled={editSluSubmitting}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    this.setState({ editSluNameDraft: e.currentTarget.value });
                                }}
                            />
                        </FormFieldCell>
                    </Box>
                </DialogComponent>
            </Box>
        );
    }
}

export default ParkModeProjectBindingComponent;

type Props = Record<string, never>;

type State = {
    menuData: SecondMenu[];
    activeMenuIndex?: string;
    firstMenuId?: number;
    taxGroup: TaxGroup[];
    isLoading: boolean;
    printGroup: any[];
    isEditSluDialogOpen: boolean;
    editSluTargetId: number | null;
    editSluNameDraft: string;
    editSluSubmitting: boolean;
};

/** 项目绑定：菜单列表 + 详情区加载骨架（与本页 `Grid` 子项结构一致） */
function ParkModeProjectBindingSkeletonGrid(): React.ReactElement {
    return (
        <>
            <Grid item>
                <Box className="park-mode-project-binding__skeleton-menu">
                    <Skeleton variant="rounded" width={220} height={48} />
                    <Skeleton variant="rounded" width={220} height={40} />
                    <Skeleton variant="rounded" width={220} height={40} />
                    <Skeleton variant="rounded" width={220} height={40} />
                    <Skeleton variant="rounded" width={220} height={40} />
                    <Skeleton variant="rounded" width={220} height={40} />
                </Box>
            </Grid>
            <Grid item xs>
                <Box className="park-mode-project-binding__skeleton-detail">
                    <Skeleton variant="rounded" width="100%" height={56} />
                    <Skeleton variant="rounded" width="100%" height={120} />
                    <Skeleton variant="rounded" width="100%" height={200} />
                </Box>
            </Grid>
        </>
    );
}
